import React, { useState, useEffect } from 'react';
import { Card, Skeleton, Button } from '../../components/ui';
import { useInfluencerAuth } from '../../contexts/InfluencerAuthContext';
import { influencerAPI } from '../../services/influencerApi';
import {
  TrendingUp,
  People,
  LocalOffer,
  MonetizationOn,
  CalendarMonth,
  CheckCircle,
  AccessTime,
  Warning
} from '../../mui-icons';
import './InfluencerDashboardPage.css';

const InfluencerDashboardPage = () => {
  const { influencer } = useInfluencerAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await influencerAPI.getDashboard();
        if (response.status === 'success') {
          setDashboard(response.data);
        }
      } catch (err) {
        console.error('Erro ao carregar dashboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="influencer-dashboard-page">
        <Skeleton width={280} height={28} borderRadius={4} />
        <Skeleton width={200} height={16} borderRadius={4} style={{ marginTop: 8, marginBottom: 24 }} />
        <div className="summary-grid">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} variant="default" className="dashboard-skeleton-stat">
              <Skeleton width="100%" height={72} borderRadius={12} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="influencer-dashboard-page">
        <div className="error-state">
          <Warning sx={{ fontSize: 48 }} />
          <p>{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="influencer-dashboard-page">
      {/* Welcome */}
      <div className="welcome-section">
        <h1>Olá, {influencer?.name || dashboard?.influencer?.name}! 👋</h1>
        <p>Confira suas métricas e comissões</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <Card
          variant="stat"
          highlight
          icon={<MonetizationOn />}
          title="Comissão Pendente"
          className="summary-card"
        >
          <span className="pn-card__stat-value">
            {formatCurrency(dashboard?.commission_history?.pending_payment)}
          </span>
        </Card>

        <Card variant="stat" icon={<People />} title="Usuários Convertidos" className="summary-card">
          <span className="pn-card__stat-value">{dashboard?.summary?.total_users_converted || 0}</span>
        </Card>

        <Card variant="stat" icon={<TrendingUp />} title="Assinaturas Ativas" className="summary-card">
          <span className="pn-card__stat-value">{dashboard?.summary?.active_subscriptions || 0}</span>
        </Card>

        <Card variant="stat" icon={<CheckCircle />} title="Total Recebido" className="summary-card">
          <span className="pn-card__stat-value">
            {formatCurrency(dashboard?.commission_history?.total_paid)}
          </span>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-columns">
        {/* Left Column */}
        <div className="dashboard-column">
          {/* Coupons Stats */}
          <Card variant="default" className="dashboard-card" title={<><LocalOffer /> Meus Cupons</>}>
            <div className="coupons-list">
              {dashboard?.coupons_stats?.length > 0 ? (
                dashboard.coupons_stats.map(coupon => (
                  <div key={coupon.idcoupon} className="coupon-item">
                    <div className="coupon-header">
                      <span className="coupon-code">{coupon.code}</span>
                      <span className={`coupon-status ${coupon.active ? 'active' : 'inactive'}`}>
                        {coupon.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="coupon-stats">
                      <div className="coupon-stat">
                        <span className="stat-label">Usos</span>
                        <span className="stat-value">{coupon.total_uses || 0}</span>
                      </div>
                      <div className="coupon-stat">
                        <span className="stat-label">Comissão</span>
                        <span className="stat-value">{coupon.commission_percent}%</span>
                      </div>
                      <div className="coupon-stat">
                        <span className="stat-label">Meses</span>
                        <span className="stat-value">{coupon.commission_months}</span>
                      </div>
                      {coupon.max_uses && (
                        <div className="coupon-stat">
                          <span className="stat-label">Restantes</span>
                          <span className="stat-value">{coupon.remaining_uses}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-message">Nenhum cupom cadastrado</p>
              )}
            </div>
          </Card>

          <Card variant="default" className="dashboard-card" title={<><TrendingUp /> Projeção de Comissão</>}>
            {dashboard?.commission_projection?.next_3_months?.length > 0 ? (
              <>
                <div className="projection-list">
                  {dashboard.commission_projection.next_3_months.map((proj, index) => (
                    <div key={index} className="projection-item">
                      <span className="projection-month">{proj.month}</span>
                      <span className="projection-value">{formatCurrency(proj.estimated)}</span>
                    </div>
                  ))}
                </div>
                <div className="projection-total">
                  <span>Total Projetado (3 meses):</span>
                  <strong>{formatCurrency(dashboard.commission_projection.total_projected)}</strong>
                </div>
              </>
            ) : (
              <p className="empty-message">Nenhuma projeção disponível</p>
            )}
          </Card>
        </div>

        <div className="dashboard-column">
          <Card variant="default" className="dashboard-card" title={<><People /> Usuários com seu Código</>}>
            <div className="users-list">
              {dashboard?.users_with_code?.list?.length > 0 ? (
                dashboard.users_with_code.list.slice(0, 10).map((user, index) => (
                  <div key={index} className="user-item">
                    <div className="user-info">
                      <span className="user-name">{user.name}</span>
                      <span className="user-coupon">{user.coupon_code}</span>
                    </div>
                    <div className="user-status">
                      <span className={`subscription-status ${user.subscription_status}`}>
                        {user.subscription_status === 'active' && 'Ativo'}
                        {user.subscription_status === 'inactive' && 'Inativo'}
                        {user.subscription_status === 'trial' && 'Trial'}
                        {user.subscription_status === 'cancelled' && 'Cancelado'}
                      </span>
                      <span className="user-date">{formatDate(user.registered_at)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-message">Nenhum usuário registrado</p>
              )}
            </div>
            {dashboard?.users_with_code?.total > 10 && (
              <p className="list-more">
                +{dashboard.users_with_code.total - 10} usuários
              </p>
            )}
          </Card>

          <Card variant="default" className="dashboard-card" title={<><CalendarMonth /> Histórico de Pagamentos</>}>
            <div className="payments-list">
              {dashboard?.commission_history?.payments?.length > 0 ? (
                dashboard.commission_history.payments.slice(0, 5).map((payment, index) => (
                  <div key={index} className="payment-item">
                    <div className="payment-info">
                      <span className="payment-month">{payment.month}</span>
                      <span className={`payment-status ${payment.status}`}>
                        {payment.status === 'paid' ? <CheckCircle sx={{ fontSize: 14 }} /> : <AccessTime sx={{ fontSize: 14 }} />}
                        {payment.status === 'paid' ? 'Pago' : 'Pendente'}
                      </span>
                    </div>
                    <div className="payment-value">
                      {formatCurrency(payment.amount)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-message">Nenhum pagamento registrado</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Card variant="default" className="dashboard-card full-width" title={<><TrendingUp /> Resumo de Assinaturas</>}>
        <div className="subscriptions-summary">
          <div className="subscription-stat">
            <span className="stat-number">{dashboard?.subscriptions?.total_generated || 0}</span>
            <span className="stat-label">Total Gerados</span>
          </div>
          <div className="subscription-stat active">
            <span className="stat-number">{dashboard?.subscriptions?.active || 0}</span>
            <span className="stat-label">Ativos</span>
          </div>
          <div className="subscription-stat trial">
            <span className="stat-number">{dashboard?.subscriptions?.trial || 0}</span>
            <span className="stat-label">Em Trial</span>
          </div>
          <div className="subscription-stat cancelled">
            <span className="stat-number">{dashboard?.subscriptions?.cancelled || 0}</span>
            <span className="stat-label">Cancelados</span>
          </div>
          <div className="subscription-stat inactive">
            <span className="stat-number">{dashboard?.subscriptions?.inactive || 0}</span>
            <span className="stat-label">Inativos</span>
          </div>
        </div>
        {dashboard?.summary?.conversion_rate && (
          <div className="conversion-rate">
            Taxa de Conversão: <strong>{dashboard.summary.conversion_rate}</strong>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InfluencerDashboardPage;

